import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {fontSizes} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';
import {Workspace} from '../../components/inboxes/workspaces/workspace';
import {AuditTrailFiltersMenuSectionHeader} from './auditTrailsFilterMenuSectionHeader';

/*
 * Types.
 */

export interface AuditTrailCategoryElement {
  id: string;
  name: Renderer;
}

interface AuditTrailCategoryVisualElement extends AuditTrailCategoryElement {
  isSelected: boolean;
}

/*
 * Props.
 */

interface AuditTrailFilterSectionProps {
  /** Section title */
  name: Renderer;
  /** Section items */
  elements: ReadonlyArray<AuditTrailCategoryElement>;
  /** Potential preselected state (use to restore view upon reload) */
  preselection: ReadonlyArray<string> | undefined;
  /** Onclick handler */
  selectionChanged: (arg: ReadonlyArray<string> | undefined) => void;
}

/*
 * Styles.
 */

const StyledCategoryContainer = styled.div`
  margin-bottom: 10px;
`;

const StyledCategoryItem = styled.div`
  display: flex;
  height: 28px;
  line-height: 28px;
  margin: 0 20px;
  line-height: normal;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.leftBar.item};
  align-items: center;
`;

const StyledCategoryItemDescription = styled.div`
  flex: 1;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const AuditTrailFilterSection: FC<AuditTrailFilterSectionProps> = (props) => {
  const {name, elements, preselection, selectionChanged} = props;
  const [isExpanded, setIsExpanded] = useState(true);

  const unifiedRenderer = useCallback(() => <Trans id={`unified-${name}`}>All</Trans>, [name]);

  const [items, setItems] = useState(() => {
    if (!preselection || preselection.length === 0) {
      return [
        {id: 'unified', name: unifiedRenderer, isSelected: true},
        ...elements.map((e) => ({...e, isSelected: false})),
      ];
    }

    return [
      {id: 'unified', name: unifiedRenderer, isSelected: false},
      ...elements.map((e) => ({...e, isSelected: preselection.includes(e.id)})),
    ];
  });

  const toggleSection = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const elementWasClicked = useCallback(
    (element: AuditTrailCategoryVisualElement) => {
      const newState = recomputeState(items, element);
      // bit of logic to get the new state depending on the element that was clicked.
      setItems(newState);

      //  items[0] is unified
      if (newState[0].isSelected) {
        selectionChanged(undefined);
      }

      const filters = newState
        .filter((item) => item.id !== 'unified' && item.isSelected === true)
        .map((item) => item.id);
      selectionChanged(filters);
    },
    [items, selectionChanged],
  );

  function recomputeState(
    stateItems: ReadonlyArray<AuditTrailCategoryVisualElement>,
    element: AuditTrailCategoryVisualElement,
  ) {
    // if this is the unified element, select all or unselect all.
    // if this is another element:
    // toggle element. maybe toggle all.
    if (element.id === 'unified') {
      const isUnifiedSelected = !element.isSelected;
      // killall switch.
      return stateItems.map((e) => {
        if (e.id === 'unified') {
          return {...e, isSelected: isUnifiedSelected};
        }
        return {...e, isSelected: !isUnifiedSelected};
      });
    }

    // non unified case
    // start by changing the element that was clicked.
    const newItems = stateItems.map((e) => {
      if (e.id !== element.id) {
        return e;
      }
      return {...e, isSelected: !e.isSelected};
    });

    const [unified, ...rest] = newItems;
    const isValid = rest.some((e) => e.isSelected);
    if (isValid) {
      return [{...unified, isSelected: false}, ...rest];
    }

    return [{...unified, isSelected: true}, ...rest];
  }

  return (
    <StyledCategoryContainer>
      <Workspace isExpanded={isExpanded}>
        {/* Header. */}
        <AuditTrailFiltersMenuSectionHeader isExpanded={isExpanded} onToggleExpandClick={toggleSection}>
          {name()}
        </AuditTrailFiltersMenuSectionHeader>

        {/* Section content. */}
        {isExpanded && renderSectionContent(items, elementWasClicked)}
      </Workspace>
    </StyledCategoryContainer>
  );
};

function renderSectionContent(
  elements: ReadonlyArray<AuditTrailCategoryVisualElement>,
  onItemClick: (element: AuditTrailCategoryVisualElement) => void,
) {
  return (
    <>
      {elements.map((el) => (
        <StyledCategoryItem key={el.id}>
          <StyledCategoryItemDescription onClick={() => onItemClick(el)}>
            {el.name()}
          </StyledCategoryItemDescription>
          <CheckBox isChecked={el.isSelected} onChange={() => onItemClick(el)} />
        </StyledCategoryItem>
      ))}
    </>
  );
}
